import React, { useMemo, useState } from "react";
import { Button, Card } from "@sscs/ui";
import { convertSecondsToDays } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo } from "Hooks";
import { GetOrganizationUsageResponse } from "Types";
import { getHumanReadableOrgLimits } from "../../utils";
import AnalysisUsageBar from "../AnalysisUsageBar";
import ContentCard from "../ContentCard/ContentCard";
import DownloadUsageBar from "../DownloadUsageBar";
import HistoricalUsageChart from "../HistoricalUsageChart/HistoricalUsageChart";
import PackageUsageBar from "../PackageUsageBar";
import ReallocateQuotas from "../ReallocateQuotas/ReallocateQuotas";
import styles from "./header.module.scss";

type HeaderProps = {
  data?: GetOrganizationUsageResponse;
};

const Header = ({ data }: HeaderProps): React.JSX.Element | null => {
  const [increaseCapacity, setIncreaseCapacity] = useState(false);

  const canReallocateAnalysisCapacity = useAllowedTo({
    permissions: [PERMISSION.REALLOCATE_QUOTAS],
  });

  const {
    analysisCapacityPortalAndCli,
    analysisCapacityPortal,
    analysisCapacityCli,
    analysisFlexCapacity,
    max_package_size,
  } = useMemo(() => getHumanReadableOrgLimits(data), [data]);

  if (!data) return null;

  const daysToQuotaReset =
    convertSecondsToDays(data.time_to_capacity_reset_seconds) + 1;

  return (
    <Card.Card fullWidth>
      <Card.Title
        as="h2"
        details={[data.organization_name]}
        actions={
          canReallocateAnalysisCapacity && (
            <Button color="primary" onClick={() => setIncreaseCapacity(true)}>
              Reallocate analysis capacity
            </Button>
          )
        }
      >
        Monthly analysis capacity
      </Card.Title>
      <div className={styles.root}>
        <ContentCard variant="main">
          <div className={styles.summary}>
            <div className={styles["summary-main-info"]}>
              <div className={styles["analysis-capacity"]}>
                {analysisCapacityPortalAndCli}
              </div>
              <div className={styles["portal-and-cli-quota"]}>
                Portal {analysisCapacityPortal}/CLI {analysisCapacityCli}
              </div>
              {data.organization_flex_capacity_bytes > 0 && (
                <div className={styles["flex-capacity"]}>
                  Flex (+{analysisFlexCapacity})
                </div>
              )}
            </div>
            <div className={styles["quota-reset"]}>
              Analysis capacity will reset in{" "}
              <strong>
                {daysToQuotaReset.toLocaleString()} day
                {daysToQuotaReset === 1 ? "" : "s"}
              </strong>
            </div>
            <div className={styles["upload-size"]}>
              Maximum file upload size <strong>{max_package_size} GB</strong>
            </div>
          </div>
        </ContentCard>
        <ContentCard variant="chart">
          <div className={styles["historical-usage"]}>
            <HistoricalUsageChart history={data.historical_usage} />
          </div>
        </ContentCard>
        <ContentCard variant="analysis-capacity">
          <strong>Analysis capacity</strong>{" "}
          <div className={styles["bar"]}>
            <AnalysisUsageBar
              portalUsage={data.organization_used_this_month_bytes_portal}
              cliUsage={data.organization_used_this_month_bytes_cli}
              totalCapacity={
                (data?.allocation_capacity_bytes_portal ?? 0) +
                (data?.allocation_capacity_bytes_cli ?? 0) +
                data?.organization_flex_capacity_bytes
              }
            />
          </div>
        </ContentCard>
        <ContentCard variant="download-capacity">
          <strong>Portal download limit</strong>{" "}
          <div className={styles["bar"]}>
            <DownloadUsageBar
              capacity={data.download_capacity_bytes}
              used={data.organization_downloaded_this_month_bytes}
            />
          </div>
        </ContentCard>
        <ContentCard variant="package-limit">
          <strong>Portal package limit</strong>{" "}
          <div className={styles["bar"]}>
            <PackageUsageBar
              capacity={data.max_packages_per_organization}
              used={data.organization_package_count}
            />
          </div>
        </ContentCard>
        {canReallocateAnalysisCapacity && (
          <ReallocateQuotas
            totalCapacityBytes={
              data.allocation_capacity_bytes_portal +
              data.allocation_capacity_bytes_cli
            }
            portalCapacityBytes={data.allocation_capacity_bytes_portal}
            portalUsageBytes={data.organization_used_this_month_bytes_portal}
            cliTotalAllocationBytes={data.total_site_key_allocation_bytes}
            isOpen={increaseCapacity}
            onClose={() => setIncreaseCapacity(false)}
          />
        )}
      </div>
    </Card.Card>
  );
};

export default Header;
